import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { CardsSkeleton } from '@/components/ui/skeleton';
import { apiClient } from '@/lib/api-client';
import { formatAmountMinor, resolveFinanceLocale } from './finance-format';
import { isRecord } from './finance-view-guards';
import { useFinanceView } from './useFinanceView';

type CategoryTotal = {
  categoryCode: string;
  categoryName: string;
  amountMinor: number;
  movementCount: number;
};

type DividendYear = {
  fiscalYear: number;
  amountMinor: number;
  movementCount: number;
  actualMinor: number;
  forecastMinor: number;
  byCategory: CategoryTotal[];
};

type DividendsView = {
  organizationId: string;
  declaredCategoryCodes: string[];
  declared: boolean;
  declaredBy: string;
  years: DividendYear[];
  totalMinor: number;
  actualTotalMinor: number;
};

function isCategoryTotal(value: unknown): value is CategoryTotal {
  return (
    isRecord(value) &&
    typeof value.categoryCode === 'string' &&
    typeof value.categoryName === 'string' &&
    typeof value.amountMinor === 'number' &&
    typeof value.movementCount === 'number'
  );
}

function isDividendYear(value: unknown): value is DividendYear {
  return (
    isRecord(value) &&
    typeof value.fiscalYear === 'number' &&
    typeof value.amountMinor === 'number' &&
    typeof value.movementCount === 'number' &&
    typeof value.actualMinor === 'number' &&
    typeof value.forecastMinor === 'number' &&
    Array.isArray(value.byCategory) &&
    value.byCategory.every(isCategoryTotal)
  );
}

function isDividendsView(value: unknown): value is DividendsView {
  return (
    isRecord(value) &&
    typeof value.organizationId === 'string' &&
    Array.isArray(value.declaredCategoryCodes) &&
    value.declaredCategoryCodes.every((code) => typeof code === 'string') &&
    typeof value.declared === 'boolean' &&
    typeof value.declaredBy === 'string' &&
    Array.isArray(value.years) &&
    value.years.every(isDividendYear) &&
    typeof value.totalMinor === 'number' &&
    typeof value.actualTotalMinor === 'number'
  );
}

/**
 * What the company has distributed to its members, by accrual year.
 *
 * WHY IT IS ITS OWN SCREEN (Human decision 35, 2026-09-14: «la schermata dei dividendi ci deve
 * essere»). The distributions were already in the ledger and already visible on four of the six
 * screens as ordinary outflows. What did not exist was a way to show or hide them as a thing —
 * the movement filter is equality-only and cannot exclude, so "everything except distributions"
 * was not expressible. A separate screen behind its own permission makes it expressible.
 *
 * WHAT IT DELIBERATELY DOES NOT SHOW. No per-member figure — and since 2026-09-21 that is a
 * ruling rather than a precaution. Decision 36 recorded that the Human had ASKED whether a
 * managing director normally sees dividends rather than ruling; decision 40 answers it,
 * verbatim: «totali sì, nomi no». So the aggregate shape is what was chosen, not a holding
 * position. A per-member breakdown would additionally need a member registry, which does not
 * exist. The screen says so in its own text rather than leaving a reader to wonder.
 *
 * WHAT THE RULING DOES NOT REACH, stated here because this screen is where a reader will look
 * for it. «Nomi no» is not enforced by withholding this screen: the distributions are ordinary
 * rows of the movement register, that register accepts a `category` filter and returns each
 * movement's description, and a holder of `finance:movements:read` can therefore read them
 * per-person. This screen even displays the declared category codes. The exposure predates this
 * screen — the movement register already carried individual salaries — and closing it would mean
 * putting individual figures behind their own permission, which nobody has decided.
 */

type Amount = (valueMinor: number) => string;

function YearCard({ year, amount }: { readonly year: DividendYear; readonly amount: Amount }) {
  const { t } = useTranslation('finance');
  return (
    <li
      className="border-border rounded-md border p-4"
      data-testid={`dividends-year-${year.fiscalYear}`}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-base font-semibold">
          {t('dividends.year', { year: year.fiscalYear })}
        </h3>
        <span className="text-lg font-medium">{amount(year.amountMinor)}</span>
      </div>
      {year.forecastMinor !== 0 && (
        <p className="text-foreground/60 mt-1 text-xs">
          {t('dividends.split', {
            paid: amount(year.actualMinor),
            planned: amount(year.forecastMinor),
          })}
        </p>
      )}
      <ul className="mt-2 space-y-1 text-sm">
        {year.byCategory.map((category) => (
          <li key={category.categoryCode} className="flex justify-between gap-4">
            <span>{category.categoryName}</span>
            <span className="text-foreground/60">
              {amount(category.amountMinor)} ·{' '}
              {t('dividends.movementCount', { count: category.movementCount })}
            </span>
          </li>
        ))}
      </ul>
    </li>
  );
}

function DividendsDeclared({
  data,
  amount,
}: {
  readonly data: DividendsView;
  readonly amount: Amount;
}) {
  const { t } = useTranslation('finance');
  return (
    <>
      <div className="flex flex-wrap gap-6" data-testid="dividends-totals">
        <div>
          <p className="text-foreground/60 text-xs">{t('dividends.totals.paid')}</p>
          <p className="text-2xl font-semibold">{amount(data.actualTotalMinor)}</p>
        </div>
        {data.totalMinor !== data.actualTotalMinor && (
          <div>
            <p className="text-foreground/60 text-xs">{t('dividends.totals.withPlanned')}</p>
            <p className="text-2xl font-semibold">{amount(data.totalMinor)}</p>
          </div>
        )}
      </div>

      {data.years.length === 0 ? (
        <p className="text-sm" data-testid="dividends-none">
          {t('dividends.noneDistributed', {
            categories: data.declaredCategoryCodes.join(', '),
          })}
        </p>
      ) : (
        <ul className="space-y-4">
          {data.years.map((year) => (
            <YearCard key={year.fiscalYear} year={year} amount={amount} />
          ))}
        </ul>
      )}
    </>
  );
}

export function FinanceDividendsPage() {
  const { t, i18n } = useTranslation('finance');
  const locale = resolveFinanceLocale(i18n.resolvedLanguage);
  const amount = useCallback(
    (valueMinor: number) => formatAmountMinor(valueMinor, locale),
    [locale],
  );

  const request = useCallback(() => apiClient.GET('/v1/finance/dividends'), []);
  const view = useFinanceView(request, isDividendsView);

  if (view.status === 'loading') {
    return <CardsSkeleton />;
  }

  if (view.status === 'error') {
    return (
      <div className="p-6">
        <p className="text-sm">{t('dividends.loadError')}</p>
        <Button className="mt-3" onClick={view.reload}>
          {t('dividends.retry')}
        </Button>
      </div>
    );
  }

  const data = view.data;

  return (
    <section className="space-y-6 p-6">
      <div>
        <h2 className="screen-title">{t('dividends.title')}</h2>
        <p className="text-foreground/60 mt-1 max-w-3xl text-sm">{t('dividends.subtitle')}</p>
      </div>

      {/*
        THE STATE THAT MUST NOT READ AS ZERO.

        "Nothing was distributed" and "nobody has said which categories are distributions" produce
        the same number and mean opposite things. The prototype never faced the question because
        it found its dividend category by a regular expression over the display name — the first
        of the thirteen catalogued defects, and the one that breaks when somebody renames a row.
        This platform did not port that, so the categories are declared, and a screen that has not
        been told says so instead of showing a confident zero.
      */}
      {!data.declared ? (
        <div
          className="rounded-md border border-amber-200 bg-amber-50/50 p-4"
          data-testid="dividends-undeclared"
        >
          <p className="text-sm font-medium">{t('dividends.undeclared.heading')}</p>
          <p className="mt-1 max-w-3xl text-sm">
            {t('dividends.undeclared.explain', { parameter: data.declaredBy })}
          </p>
        </div>
      ) : (
        <DividendsDeclared data={data} amount={amount} />
      )}

      <p className="text-foreground/60 max-w-3xl text-xs" data-testid="dividends-aggregate-note">
        {t('dividends.aggregateOnly')}
      </p>
    </section>
  );
}
